import {
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  Table,
  TableHead,
  TableBody,
  TableContainer,
  Paper,
  TablePagination,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Course } from "../../model/entity/Course";
import {
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ThumbDown,
  ThumbUp,
} from "@mui/icons-material";
import { OverviewFilterField } from "../../components/OverviewFilterField";
import { useNavigate } from "react-router-dom";
import handleAxiosError from "../../components/ErrorHandler";
import { notifErr, notifWarn } from "../../components/Functions";
import { TrainingSchedule } from "../../model/entity/TrainingSchedule";
import { getFetcher } from "../../components/Fetchers";

function Row({
  course,
  allOpen,
  onEdit,
}: {
  course: Course;
  allOpen: boolean;
  onEdit: () => void;
}) {
  const [open, setOpen] = useState(allOpen);

  useEffect(() => {
    setOpen(allOpen);
  }, [allOpen]);

  return (
    <React.Fragment>
      <TableRow key={course.id} className={"collapsibleTableRow"}>
        <TableCell className="tableCell tableFirstCell" width={"23%"}>
          {
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          }
          {course.name}
        </TableCell>
        <TableCell className="tableCell" width={"25%"}>
          {course.memberInCourses.length + " / " + course.capacity}
        </TableCell>
        <TableCell className="tableCell">{course.fee + "Kč"}</TableCell>
        <TableCell className="tableCell">
          {course.memberInCourses.some((mic) => !mic.paid) ? (
            <ThumbDown className="thumbDown" />
          ) : (
            <ThumbUp className="thumbUp" />
          )}
        </TableCell>
        <TableCell className="tableCell tableLastCell" onClick={onEdit}>
          <Edit className="editBtn" />
        </TableCell>
      </TableRow>
      {open && course.memberInCourses.length > 0 && (
        <TableRow>
          <TableCell sx={{ borderBottom: "none" }} colSpan={2}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", border: "none" }}>
                        Jméno
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", border: "none" }}
                      >
                        Zaplatil
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {course.memberInCourses.map((mic) => (
                      <TableRow
                        key={mic.id}
                        sx={{
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <TableCell>
                          {mic.member.firstName + " " + mic.member.lastName}
                        </TableCell>
                        <TableCell align="right">
                          {mic.paid ? (
                            <ThumbUp className="thumbUp" sx={{marginRight: "0.75rem"}}/>
                          ) : (
                            <ThumbDown className="thumbDown" sx={{marginRight: "0.75rem"}}/>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}

const CoursesOverview = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [courses, setCourses] = useState<Course[]>([]);
  const [trainingSchedule, setTrainingSchedule] =
    useState<TrainingSchedule | null>(null);
  const [totalElements, setTotalElements] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filter, setFilter] = useState("");
  const [allOpen, setAllOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrainingSchedule = async () => {
      try {
        const response = await getFetcher(
          `${process.env.REACT_APP_API_URL}/schedules/active`
        );
        setTrainingSchedule(response.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
      setLoading(false);
    };
    fetchTrainingSchedule();
  }, []);

  useEffect(() => {
    if (!loading && trainingSchedule === null) {
      notifWarn(
        "Nebyl nalezen žádný tréninkový rozvrh. Pro přidání" +
          " nebo úpravu kurzů vytvořte nový rozvrh v sekci\n" +
          "Tréninkový rozvrh - Nastavení.",
        null
      );
    }
  }, [loading, trainingSchedule]);

  useEffect(() => {
    const fetchCourses = async () => {
      const config = {
        search: filter,
        page: page,
        size: rowsPerPage,
      };
      try {
        const response = await getFetcher(
          `${process.env.REACT_APP_API_URL}/courses/all-active`,
          config
        );
        setCourses(response.data.content);
        setTotalElements(response.data.totalElements);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };

    fetchCourses();
  }, [page, rowsPerPage, filter]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="tableContainer">
      <div className="aboveTableWrapper">
        <div>
          <Button
            className="submitBtn"
            disabled={trainingSchedule === null}
            onClick={() => {
              navigate("/overview/courses/add-new");
            }}
          >
            Nový kurz
          </Button>
          <FormControlLabel
            control={<Checkbox />}
            disabled={trainingSchedule === null}
            checked={allOpen}
            label="Rozbalit vše"
            onChange={() => setAllOpen(!allOpen)}
            sx={{ marginLeft: "0.5rem" }}
          />
        </div>
        <div>
          <OverviewFilterField
            setFilter={setFilter}
            disabled={trainingSchedule === null}
          />
        </div>
      </div>
      <Paper
        className="tableContainer"
        elevation={courses.length ? 1 : 0}
        sx={{ width: "70%", maxHeight: "48rem" }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow className="tableHeader">
                <TableCell className="tableHeaderCell tableHeaderFirstCell">
                  Název
                </TableCell>
                <TableCell className="tableHeaderCell">Obsazenost</TableCell>
                <TableCell className="tableHeaderCell">Příspěvky</TableCell>
                <TableCell className="tableHeaderCell">Zaplaceno</TableCell>
                <TableCell className="tableHeaderCell tableHeaderLastCell">
                  Akce
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {courses.map((course) => (
                <Row
                  key={course.id}
                  course={course}
                  allOpen={allOpen}
                  onEdit={() => navigate(`/overview/courses/edit/${course.id}`)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ backgroundColor: "white", width: "100%" }}>
          <TablePagination
            className="tablePagination"
            rowsPerPageOptions={[5, 10, 20, 999]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
    </div>
  );
};

export default CoursesOverview;
